import {
  captureException,
  captureMessage,
  setUser,
  addBreadcrumb,
  setTag,
  withScope,
} from "@sentry/react-native"
import { ScopeContext } from "@sentry/types"

import { debugFactory } from "@treefort/lib/debug"
import { DisplayableError } from "@treefort/lib/displayable-error"
import { isAxiosError } from "@treefort/lib/is-axios-error"

import config from "../config"
import { toast } from "./toaster"

export function setCurrentUserId(userId: string | null) {
  if (userId) {
    addBreadcrumb({
      category: "auth",
      message: "Authenticated user " + userId,
      level: "info",
    })
    setUser({ id: userId })
  } else {
    setUser(null)
  }
}

export function setCurrentProfileId(profileId: string | null) {
  setTag("profileId", profileId ?? null)
}

/**
 * Returns true if an error represents a failed network request as indicated by
 * an axios error object with a request field but no response field. See:
 * https://axios-http.com/docs/handling_errors.
 */
export function isAxiosNetworkError(error: unknown) {
  return isAxiosError(error) && error.request && !error.response
}

/**
 * Log an error somewhere easily accessible (e.g. send to Sentry in production,
 * drop in the console during development). If the error is an instance of
 * DisplayableError it will also be presented to the user.
 */
export const logError = (
  error: Parameters<typeof captureException>[0],
  captureContext?: Partial<ScopeContext>,
): void => {
  const displayable = error instanceof DisplayableError
  // Only log displayable errors if they have a cause associated with them
  if (!displayable || error.cause) {
    captureException(convertStringsToErrors(error), {
      ...captureContext,
      tags: { ...captureContext?.tags, displayable },
    })
  }
  if (displayable) {
    toast.error(error.message)
  }
}

/**
 * Log a warning (a low priority error)
 */
export const logWarning = (
  error: Parameters<typeof captureException>[0],
): void => {
  withScope((scope) => {
    scope.setLevel("warning")
    logError(error)
  })
}

/**
 * Log something other than an error
 */
export const logMessage = captureMessage

export const debug = debugFactory("app", config)

/**
 * Sentry appears to have trouble with string errors. This can help with that.
 *
 * NOTE: This may be unnecessary in newer versions of @sentry/react-native.
 * See: https://github.com/getsentry/sentry-react-native/issues/3673
 */
function convertStringsToErrors(error: unknown) {
  if (typeof error === "string") {
    return new Error(error)
  } else if (error instanceof Error && error.cause) {
    error.cause = convertStringsToErrors(error.cause)
    return error
  } else {
    return error
  }
}

/*
 * This tracks errors that were caught before Sentry was initialized (see
 * packages/app/web/index.html).
 */
const errorQueue =
  typeof window !== "undefined" &&
  "errorQueue" in window &&
  window.errorQueue instanceof EventTarget &&
  "errors" in window.errorQueue &&
  "messages" in window.errorQueue
    ? window.errorQueue
    : undefined

function handleQueuedErrors() {
  if (errorQueue && Array.isArray(errorQueue.errors)) {
    errorQueue.errors.forEach((error) => captureException(error))
    errorQueue.errors = []
  }
}

function handleQueuedMessages() {
  if (errorQueue && Array.isArray(errorQueue.messages)) {
    errorQueue.messages.forEach((message) => captureMessage(message))
    errorQueue.messages = []
  }
}

handleQueuedErrors()
handleQueuedMessages()

errorQueue?.addEventListener("error", handleQueuedErrors)
errorQueue?.addEventListener("message", handleQueuedMessages)
